import sample from 'lodash.sample'
import { USER_TYPES, ACTIONS, GET_STARTED_ROUTES } from 'components/GetStarted/constants'

export const GET_STARTED_RETURN_URL_COOKIES_KEY = 'getStartedReturnUrl'

export const getHeadingForUserType = ({
  userType,
  providerHeading,
  seekerHeading,
  defaultHeading = '',
}) => {
  const subject = userType === USER_TYPES.PROVIDER ? providerHeading : seekerHeading
  return userType ? subject : defaultHeading
}

export const getStepRedirect = ({ redirectIfTests = [], userInput, data, query }) => {
  const { redirectTo } =
    redirectIfTests.find((item) =>
      item.test.every((predicate) => predicate({ userInput, query, data }))
    ) || {}
  const redirectPath = typeof redirectTo === 'function' ? redirectTo(data) : redirectTo
  const isUrlAbsolute = !Object.values(GET_STARTED_ROUTES).some((path) => path === redirectPath)
  return { redirectPath, isUrlAbsolute }
}

export const isLoggedIn = ({ data }) => !!data?.viewer?.user

export const isAnonymousUser = ({ data }) => !isLoggedIn({ data })

export const isSubscribed = ({ userInput, data }) =>
  !!data?.viewer?.is_subscribed || !!userInput?.isSubscribed

export const multipleListingsEnabled = ({ data }) =>
  isLoggedIn({ data }) &&
  data?.viewer?.site?.multiple_seeker_listings &&
  data?.viewer?.user?.user_type?.toUpperCase() === USER_TYPES.SEEKER

export const multipleListingsDisabled = ({ data }) => !multipleListingsEnabled({ data })

export const hasActiveListing = ({ data }) => !!data?.viewer?.user?.has_visible_listing

export const hasNoActiveListing = ({ data }) => !hasActiveListing({ data })

export const isSponsorshipDisabled = ({ data }) => !!data?.site?.supports_sponsorship === false

export const hasSelectedBasicPlan = ({ userInput }) => userInput?.payment_plan_id === null

export const isContactInfoPrevStepIncomplete = ({ userInput }) => !!userInput?.user_type === false

export const isEnterNamePrevStepIncomplete = ({ userInput }) =>
  isContactInfoPrevStepIncomplete({ userInput }) ||
  !!userInput.phone_number === false ||
  !!userInput.email === false

export const isCreateUserPrevStepIncomplete = ({ userInput }) =>
  isEnterNamePrevStepIncomplete({ userInput }) ||
  !!userInput?.first_name === false ||
  !!userInput?.last_name === false

export const isSponsorshipPrevStepIncomplete = ({ userInput, data }) => {
  const isCanadaOnly = !!data?.site?.canada_only
  const hasCountry = !!userInput?.country
  const hasState = !!userInput?.state
  const hasCity = !!userInput?.city
  return isCanadaOnly ? !hasState || !hasCity : !hasCountry || !hasState || !hasCity
}

export const isServicesPrevStepIncomplete = ({ userInput, data }) => {
  const isPrevStepIncomplete = isSponsorshipDisabled({ data })
    ? false
    : typeof userInput?.sponsorship !== 'boolean'
  return !!(isSponsorshipPrevStepIncomplete({ userInput, data }) || isPrevStepIncomplete)
}

export const isRatePrevStepIncomplete = ({ data, userInput }) => {
  if (hasEmptyServices({ data })) {
    return false
  }
  return isServicesPrevStepIncomplete({ data, userInput }) || !!userInput?.services === false
}

export const isDetailsPrevStepIncomplete = ({ data, userInput }) => {
  const isProvider = userInput?.user_type === USER_TYPES.PROVIDER
  const isInvalidRate = isProvider ? false : !!userInput?.hourly_rate === false
  return isRatePrevStepIncomplete({ data, userInput }) || isInvalidRate
}

export const isPaymentPrevStepIncomplete = ({ userInput, query }) =>
  !!userInput?.stripePlan === false && !!query?.stripe_plan_id === false

export const isValidUserType = (value) =>
  Object.keys(USER_TYPES).some((key) => value?.toUpperCase() === USER_TYPES[key])

export const hasUserTypeInUrl = ({ query }) => isValidUserType(query?.user_type)

export const userInputReducer = (state, action) => {
  switch (action.type) {
    case ACTIONS.UPDATE_USER_TYPE_FROM_QUERY:
      return {
        ...state,
        user_type: state.user_type || action?.userType?.toUpperCase(),
      }
    case ACTIONS.UPDATE_USER_INPUT_FROM_DATA:
      return {
        ...state,
        user_type: action?.data?.viewer?.user?.user_type?.toUpperCase() || state.user_type,
        first_name: action?.data?.viewer?.user?.first_name || state.first_name,
        last_name: action?.data?.viewer?.user?.last_name || state.last_name,
      }
    default:
      return {
        ...state,
        ...action,
      }
  }
}

export const getFeaturesForStripePlan = ({
  show_cn_features: isCanadianNanny,
  show_cca_features: isCCA,
  user_type: userType,
  is_vip: isVip,
  quantity,
}) => {
  const SHARED_FEATURES = {
    TELESAFE: {
      label: 'Unlimited Protected Phone Calling with TELESAFE™',
      tooltip:
        'Securely send and receive calls on our platform without revealing your phone number.',
    },
    UNLIMITED_MESSAGES: { label: 'Unlimited Secure Private Message' },
  }
  const isProvider = userType === USER_TYPES.PROVIDER
  const isSeeker = userType === USER_TYPES.SEEKER

  if (isCanadianNanny) {
    const providerFeatures = isProvider ? [{ label: 'Featured as a Top Nanny Profile' }] : []
    const seekerFeatures = isSeeker ? [{ label: 'Featured Job Listing' }] : []
    const vipFeatures = isVip
      ? [
          { label: 'Payroll & Tax Service with Dedicated Payroll Consultant ($480 value)', isVip },
          { label: 'Candidate Background Check and Other Screening Services ($120 value)', isVip },
          {
            label:
              'Premium 1-Year Accounts on ElderCare.com, Housekeeper.com, PetSitter.com, Sitter.com, and more',
            isVip,
          },
          { label: 'Manage WSIB ($120 value)', isVip },
        ]
      : []
    return [
      ...providerFeatures,
      SHARED_FEATURES.UNLIMITED_MESSAGES,
      SHARED_FEATURES.TELESAFE,
      ...seekerFeatures,
      ...vipFeatures,
    ]
  }
  if (isCCA) {
    return [{ label: `${quantity} Active Job Posting${quantity > 1 ? 's' : ''} per Month` }]
  }
  // Default features
  return [
    SHARED_FEATURES.TELESAFE,
    SHARED_FEATURES.UNLIMITED_MESSAGES,
    { label: 'Featured Placement in Directory' },
  ]
}

export const getRandomFacetDescription = (facetNames = [], data) => {
  const randomFaceName = sample(facetNames) || {}
  const facets = data?.site?.facet_questions?.[0]?.answer_facets || []
  return facets.find((facet) => facet.name === randomFaceName)?.description ?? ''
}

export const isThankYouStep = (step) => step?.urlSlug === GET_STARTED_ROUTES.THANKYOU

export const isCCA = (domain) => domain === 'childcareadvantage.com'

export const hasEmptyServices = ({ data }) => {
  const facets = data?.site?.facet_questions?.[0]
  return !facets || facets.length === 0
}

export const hasEmptyStripePlans = ({ data }) => {
  const plans = data?.viewer?.stripe_plans?.edges ?? []
  return plans.length === 0
}

export const getNextStepIndex = ({ steps, currentPageIndex, data, userInput }) => {
  let nextStepIndex = currentPageIndex + 1
  let isNextStepAvailable = false

  // interate through steps until a non-skipped step is found
  while (!isNextStepAvailable) {
    const nextStep = steps[nextStepIndex]
    const stepSkipIfTests = nextStep?.skipIf ?? []

    if (stepSkipIfTests.length === 0) {
      isNextStepAvailable = true
    }

    const isNextStepSkipped = stepSkipIfTests.some((testFn) => testFn({ data, userInput }))
    if (!isNextStepSkipped) {
      isNextStepAvailable = true
    } else {
      nextStepIndex++
    }
  }

  return nextStepIndex
}

export const getStepIndexFromUrlSlug = (steps, stepUrlSlug) => {
  const index = steps.findIndex(({ urlSlug }) => urlSlug === stepUrlSlug)
  return index === -1 ? null : index
}

export const getLocationDropDownData = ({
  dependentField,
  dataFromSsr,
  dataFromUserInput,
  dataFromQuery,
  isLoading,
  hasDataChanged,
}) => {
  if (isLoading || !dependentField) {
    return []
  } else if (hasDataChanged) {
    return dataFromQuery || []
  } else if (dataFromUserInput) {
    return dataFromUserInput || []
  } else {
    return dataFromSsr || []
  }
}

export const getUserTimezone = () => {
  const isIntlDateTimeFormatSupported = !!(window.Intl && window.Intl.DateTimeFormat)
  return isIntlDateTimeFormatSupported ? Intl.DateTimeFormat().resolvedOptions().timeZone : null
}

export const getCityFromIpIfValidCountry = (data) => {
  const countryFromIp = data?.viewer?.current_city?.state?.country?.id
  const supportedCountries = data?.site?.supported_countries ?? []
  const isIpCountrySupported = supportedCountries.some((country) => country.id === countryFromIp)
  return isIpCountrySupported ? data?.viewer?.current_city : null
}
